import { defineStore } from 'pinia';
import { ref } from 'vue';
import { cleAdvisorApi, type CreditProgressDto, type CleAdvisorSearchResult } from '@/services/cleAdvisorApi';

export const useCleAdvisorStore = defineStore('cleAdvisor', () => {
  const summary = ref<CreditProgressDto[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);

  const searchResult = ref<CleAdvisorSearchResult | null>(null);
  const searching = ref(false);

  const cachedRecommendations = ref<CleAdvisorSearchResult | null>(null);
  const prefetching = ref(false);

  async function fetchSummary() {
    loading.value = true;
    error.value = null;
    try {
      summary.value = await cleAdvisorApi.getSummary();
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to load credit progress';
    } finally {
      loading.value = false;
    }
  }

  async function askQuestion(question: string, jurisdictionCode?: string, delivery?: string, accreditationType?: string) {
    searching.value = true;
    searchResult.value = null;
    error.value = null;
    try {
      searchResult.value = await cleAdvisorApi.search(question, jurisdictionCode, delivery, accreditationType);
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to search';
    } finally {
      searching.value = false;
    }
  }

  async function getRecommendations(delivery?: string, accreditationType?: string) {
    searching.value = true;
    searchResult.value = null;
    error.value = null;
    try {
      searchResult.value = await cleAdvisorApi.recommend(delivery, accreditationType);
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to get recommendations';
    } finally {
      searching.value = false;
    }
  }

  async function prefetchRecommendations() {
    prefetching.value = true;
    try {
      await cleAdvisorApi.prefetch();
    } catch {
      // fire-and-forget — don't surface errors
    } finally {
      prefetching.value = false;
    }
  }

  async function loadCachedRecommendations() {
    try {
      cachedRecommendations.value = await cleAdvisorApi.getCached();
    } catch {
      // ignore — cache miss is expected
    }
  }

  return {
    summary,
    loading,
    error,
    searchResult,
    searching,
    cachedRecommendations,
    prefetching,
    fetchSummary,
    askQuestion,
    getRecommendations,
    prefetchRecommendations,
    loadCachedRecommendations,
  };
});
